import { ApiService } from '../../../services/api';
import { generateAsyncActionNames } from '../../../utils/helpers';
import { CONSTANTS } from '../../../utils/constants';
import { AppDispatch } from '../../type';
import { TableActions } from '../table/table.actions';
import { getErrorMessages } from '../../../utils/formatters';
import { IApiError } from '../../../models/api';
import { GetActionPermissionsPayload } from './type';

const { get, put } = ApiService;

enum ActionPermissionsApiEndpoints {
  GET_ACTION_PERMISSIONS = `ActionPermission/GetActionPermissions`,
  GET_ACTION_PERMISSION_BY_ID = `ActionPermission/GetActionPermissionById`,
  UPDATE_ACTION_PERMISSION = `ActionPermission/UpdateActionPermission`,
}

enum ActionPermissionsActionsEnum {
  GET_ACTION_PERMISSIONS = 'GET_ACTION_PERMISSIONS',
  GET_ACTION_PERMISSION_BY_ID = 'GET_ACTION_PERMISSION_BY_ID',
  UPDATE_ACTION_PERMISSION = 'UPDATE_ACTION_PERMISSION',
}

export const ActionPermissionsActionsNames = {
  [ActionPermissionsActionsEnum.GET_ACTION_PERMISSIONS]: generateAsyncActionNames(
    ActionPermissionsActionsEnum.GET_ACTION_PERMISSIONS
  ),
  [ActionPermissionsActionsEnum.GET_ACTION_PERMISSION_BY_ID]: generateAsyncActionNames(
    ActionPermissionsActionsEnum.GET_ACTION_PERMISSION_BY_ID
  ),
  [ActionPermissionsActionsEnum.UPDATE_ACTION_PERMISSION]: generateAsyncActionNames(
    ActionPermissionsActionsEnum.UPDATE_ACTION_PERMISSION
  ),
};

const Actions = {
  [ActionPermissionsActionsEnum.GET_ACTION_PERMISSIONS]: {
    START: () => ({
      type: ActionPermissionsActionsNames.GET_ACTION_PERMISSIONS.START,
    }),
    FULFILLED: (data: GetActionPermissionsPayload) => ({
      type: ActionPermissionsActionsNames.GET_ACTION_PERMISSIONS.FULFILLED,
      payload: data,
    }),
    REJECTED: (error: IApiError[] | null) => ({
      type: ActionPermissionsActionsNames.GET_ACTION_PERMISSIONS.REJECTED,
      payload: error,
    }),
  },
  [ActionPermissionsActionsEnum.UPDATE_ACTION_PERMISSION]: {
    START: () => ({
      type: ActionPermissionsActionsNames.UPDATE_ACTION_PERMISSION.START,
    }),
    FULFILLED: () => ({
      type: ActionPermissionsActionsNames.UPDATE_ACTION_PERMISSION.FULFILLED,
    }),
    REJECTED: (error: IApiError[] | null) => ({
      type: ActionPermissionsActionsNames.UPDATE_ACTION_PERMISSION.REJECTED,
      payload: error,
    }),
  },
};

const getActionPermissionById =
  (id: number) =>
  async (dispatch: AppDispatch) => {
    try {
      const { data }: any = await get(
        `${ActionPermissionsApiEndpoints.GET_ACTION_PERMISSION_BY_ID}/${id}`
      );
      return data?.content?.actionPermission ?? null;
    } catch {
      return null;
    }
  };

const getActionPermissions =
  (
    params?: any,
    sort?: any,
    updateTable = true,
    pageNumber: number = CONSTANTS.GRIDS.FIRST_PAGE_NUMBER,
    pageSize?: number | null
  ) =>
  async (dispatch: AppDispatch, getState?: any) => {
    dispatch(
      Actions[ActionPermissionsActionsEnum.GET_ACTION_PERMISSIONS].START()
    );

    const requestData: any = {
      params,
      headers: {
        'X-Pagination': JSON.stringify({
          PageNumber: pageNumber,
          PageSize:
            pageSize ?? getState()?.table.pageSize ?? CONSTANTS.GRIDS.PAGE_SIZE,
        }),
      },
    };

    if (sort && sort.length) {
      requestData.headers['X-Sorting'] = JSON.stringify({
        Sortings: sort,
      });
    }

    try {
      const { data }: any = await get(
        ActionPermissionsApiEndpoints.GET_ACTION_PERMISSIONS,
        requestData
      );

      const actionPermissions = data?.content?.actionPermissions;

      dispatch(
        Actions[ActionPermissionsActionsEnum.GET_ACTION_PERMISSIONS].FULFILLED(
          actionPermissions
        )
      );

      if (updateTable) {
        dispatch(
          TableActions.setTableData({
            totalRows: data?.content?.totalCount,
            pageNumber: CONSTANTS.GRIDS.FIRST_PAGE_NUMBER,
          })
        );
      }
      return actionPermissions;
    } catch (error: any) {
      dispatch(
        Actions[ActionPermissionsActionsEnum.GET_ACTION_PERMISSIONS].REJECTED(
          getErrorMessages(error)
        )
      );
    }
  };

const updateActionPermission = (
  id: number,
  body: {
    status?: string | null;
    usesLimit?: number | null;
    startDate?: string | null;
    expiryDate?: string | null;
    cityId?: number | null;
    siteId?: number | null;
    cellId?: number | null;
  }
) =>
  async (dispatch: AppDispatch) => {
    dispatch(Actions[ActionPermissionsActionsEnum.UPDATE_ACTION_PERMISSION].START());
    try {
      const { data }: any = await put(
        `${ActionPermissionsApiEndpoints.UPDATE_ACTION_PERMISSION}/${id}`,
        body
      );
      if (!data.isSucceeded) throw Error(JSON.stringify(data.errors));
      dispatch(Actions[ActionPermissionsActionsEnum.UPDATE_ACTION_PERMISSION].FULFILLED());
      return data?.content;
    } catch (error: any) {
      dispatch(
        Actions[ActionPermissionsActionsEnum.UPDATE_ACTION_PERMISSION].REJECTED(
          getErrorMessages(error)
        )
      );
    }
  };

export const ActionPermissionsActions = {
  getActionPermissions,
  getActionPermissionById,
  updateActionPermission,
};